import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validates that the sum of a specific field in all FormArray controls equals a target value
 * @param targetValue The value that the sum should equal
 * @param field The name of the field to sum in each control
 * @param precision The number of decimal places to consider (defaults to 2)
 * @returns A validator function that returns null if valid, or an error object if invalid
 */
export function sumEquals(targetValue: number, field: string, precision = 2): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      throw new Error('sumEquals validator can only be used with FormArray');
    }

    const sum = control.controls.reduce((acc, curr) => {
      const value = curr.get(field)?.value || 0;
      return acc + Number(value);
    }, 0);

    // Round both numbers to handle floating point precision issues
    const roundedSum = Number(sum.toFixed(precision));
    const roundedTarget = Number(targetValue.toFixed(precision));

    if (roundedSum === roundedTarget) {
      return null;
    }

    return {
      sumEquals: {
        sum: roundedSum,
        target: roundedTarget,
        difference: roundedTarget - roundedSum,
      },
    };
  };
}

/**
 * Validates that the sum of a specific field across multiple FormArrays equals a target value
 * @param targetValue The value that the sum should equal
 * @param field The name of the field to sum in each control
 * @param arrayNames Array of form array names to sum over
 * @param precision The number of decimal places to consider (defaults to 2)
 * @returns A validator function that returns null if valid, or an error object if invalid
 */
export function sumEqualsMultiArray(
  targetValue: number,
  field: string,
  arrayNames: string[],
  precision = 2
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      throw new Error('sumEqualsMultiArray validator can only be used with FormGroup');
    }

    const sum = arrayNames.reduce((acc, arrayName) => {
      const formArray = control.get(arrayName) as FormArray;
      if (!formArray) return acc;

      return (
        acc +
        formArray.controls.reduce((arraySum, curr) => {
          const value = curr.get(field)?.value || 0;
          return arraySum + Number(value);
        }, 0)
      );
    }, 0);

    // Round both numbers to handle floating point precision issues
    const roundedSum = Number(sum.toFixed(precision));
    const roundedTarget = Number(targetValue.toFixed(precision));

    if (roundedSum === roundedTarget) {
      return null;
    }

    return {
      sumEquals: {
        sum: roundedSum,
        target: roundedTarget,
        difference: roundedTarget - roundedSum,
      },
    };
  };
}
